/**
 * AffDice SuperAdmin messages — the Brands screen, an operator's Brands section and every brand popup.
 *
 * Every key starts `brand.` so no other catalogue can collide with it.
 */
import type { Catalogue } from '@affdice/portal-kit';

export const BRANDS_MESSAGES = {
  // Brands screen (not yet built)

  // Operator's Brands section
  'brand.panel.title': { en: 'Brands', es: 'Marcas', 'pt-BR': 'Marcas' },
  'brand.panel.crossTenant': {
    en: 'A real, cross-tenant read — logged per access, not only per session.',
    es: 'Una lectura real entre inquilinos: se registra en cada acceso, no solo en cada sesión.',
    'pt-BR': 'Uma leitura real entre tenants: registrada a cada acesso, não apenas a cada sessão.',
  },
  'brand.panel.view': { en: 'View brands', es: 'Ver marcas', 'pt-BR': 'Ver marcas' },
  'brand.panel.titleFor': { en: 'Brands — {operator}', es: 'Marcas — {operator}', 'pt-BR': 'Marcas — {operator}' },
  'brand.panel.empty': { en: 'No brands yet.', es: 'Aún no hay marcas.', 'pt-BR': 'Ainda não há marcas.' },
  'brand.panel.colBrand': { en: 'Brand', es: 'Marca', 'pt-BR': 'Marca' },
  'brand.panel.colCode': { en: 'Code', es: 'Código', 'pt-BR': 'Código' },
  'brand.panel.colCurrency': { en: 'Currency', es: 'Moneda', 'pt-BR': 'Moeda' },
  'brand.panel.colDomain': { en: 'Tracking domain', es: 'Dominio de seguimiento', 'pt-BR': 'Domínio de rastreamento' },
  'brand.panel.colStatus': { en: 'Status', es: 'Estado', 'pt-BR': 'Status' },
  'brand.panel.colColour': { en: 'Colour', es: 'Color', 'pt-BR': 'Cor' },
  'brand.panel.colProducts': { en: 'Products', es: 'Productos', 'pt-BR': 'Produtos' },
  'brand.panel.standard': { en: 'Standard', es: 'Estándar', 'pt-BR': 'Padrão' },
  'brand.panel.setColour': { en: 'Set colour', es: 'Definir color', 'pt-BR': 'Definir cor' },
  'brand.panel.setLogo': { en: 'Set logo', es: 'Definir logotipo', 'pt-BR': 'Definir logotipo' },
  'brand.logo.title': { en: 'Logo for {name}', es: 'Logotipo de {name}', 'pt-BR': 'Logotipo de {name}' },
  'brand.panel.deleteLabel': { en: 'Delete {name}', es: 'Eliminar {name}', 'pt-BR': 'Excluir {name}' },

  // Brand detail popup
  'brand.detail.code': { en: 'Code', es: 'Código', 'pt-BR': 'Código' },
  'brand.detail.currency': { en: 'Currency', es: 'Moneda', 'pt-BR': 'Moeda' },
  'brand.detail.domain': { en: 'Tracking domain', es: 'Dominio de seguimiento', 'pt-BR': 'Domínio de rastreamento' },
  'brand.detail.products': { en: 'Products', es: 'Productos', 'pt-BR': 'Produtos' },
  'brand.detail.figures': { en: 'Figures', es: 'Cifras', 'pt-BR': 'Números' },
  'brand.detail.provisional': {
    en: 'Provisional — this period is not locked yet.',
    es: 'Provisional: este período aún no está bloqueado.',
    'pt-BR': 'Provisório: este período ainda não foi fechado.',
  },
  'brand.detail.asOf': { en: 'As of {date}.', es: 'A fecha de {date}.', 'pt-BR': 'Em {date}.' },
  'brand.detail.affiliatesTitle': { en: 'Affiliates on this brand', es: 'Afiliados en esta marca', 'pt-BR': 'Afiliados nesta marca' },
  'brand.detail.nobody': {
    en: 'Nobody is enrolled on this brand yet.',
    es: 'Todavía no hay nadie inscrito en esta marca.',
    'pt-BR': 'Ninguém está inscrito nesta marca ainda.',
  },
  'brand.detail.colName': { en: 'Name', es: 'Nombre', 'pt-BR': 'Nome' },
  'brand.detail.colEmail': { en: 'Email', es: 'Correo electrónico', 'pt-BR': 'E-mail' },
  'brand.detail.colCountry': { en: 'Country', es: 'País', 'pt-BR': 'País' },
  'brand.detail.colStatus': { en: 'Status', es: 'Estado', 'pt-BR': 'Status' },
  'brand.detail.colJoined': { en: 'Joined', es: 'Alta', 'pt-BR': 'Entrada' },
  'brand.detail.dormant': { en: 'Dormant', es: 'Inactivo', 'pt-BR': 'Dormente' },
  'brand.detail.openRowNote': {
    en: 'Open a row for that affiliate\'s dashboard and money on this brand alone. Their links, invoices, disputes and terms in the same popup still cover every brand.',
    es: 'Abre una fila para ver el panel y el dinero de ese afiliado solo en esta marca. Sus enlaces, facturas, disputas y condiciones en la misma ventana siguen abarcando todas las marcas.',
    'pt-BR': 'Abra uma linha para ver o painel e o dinheiro desse afiliado apenas nesta marca. Os links, faturas, contestações e termos dele na mesma janela continuam abrangendo todas as marcas.',
  },
  'brand.detail.settingsTitle': {
    en: 'Settings this brand holds its own value for',
    es: 'Ajustes para los que esta marca tiene su propio valor',
    'pt-BR': 'Configurações para as quais esta marca tem valor próprio',
  },
  'brand.detail.footnote': {
    en: 'Figures and enrolled affiliates are what the platform can read for one brand. This operator\'s campaigns, its affiliate invoices and its disputes are readable from their own console only, and a profile change is proposed here rather than made here — support reads, the operator acts.',
    es: 'Las cifras y los afiliados inscritos son lo que la plataforma puede leer de una marca. Las campañas, las facturas de afiliados y las disputas de este operador solo se pueden leer desde su propia consola, y un cambio de perfil se propone aquí en lugar de hacerse aquí: soporte lee, el operador actúa.',
    'pt-BR': 'Os números e os afiliados inscritos são o que a plataforma consegue ler de uma marca. As campanhas, as faturas de afiliados e as contestações deste operador só podem ser lidas no console dele, e uma alteração de perfil é proposta aqui em vez de feita aqui: o suporte lê, o operador age.',
  },

  // Set colour popup
  'brand.colour.title': { en: 'Set colour — {name}', es: 'Definir color — {name}', 'pt-BR': 'Definir cor — {name}' },
  'brand.colour.why': { en: 'Why', es: 'Motivo', 'pt-BR': 'Motivo' },
  'brand.colour.whyHint': {
    en: 'Optional — kept on this brand\'s own audit record.',
    es: 'Opcional: se guarda en el registro de auditoría de esta marca.',
    'pt-BR': 'Opcional: fica guardado no registro de auditoria desta marca.',
  },
  'brand.colour.optional': { en: 'Optional', es: 'Opcional', 'pt-BR': 'Opcional' },
  'brand.colour.saving': { en: 'Saving…', es: 'Guardando…', 'pt-BR': 'Salvando…' },
  'brand.colour.save': { en: 'Save colour', es: 'Guardar color', 'pt-BR': 'Salvar cor' },

  // Brand settings
  'brand.settings.setForBrand': { en: 'Set for this brand', es: 'Definido para esta marca', 'pt-BR': 'Definido para esta marca' },
  'brand.settings.inheritedOperator': {
    en: 'Inherited — the operator’s value',
    es: 'Heredado: el valor del operador',
    'pt-BR': 'Herdado: o valor do operador',
  },
  'brand.settings.inheritedPlatform': {
    en: 'Inherited — the platform default',
    es: 'Heredado: el valor predeterminado de la plataforma',
    'pt-BR': 'Herdado: o padrão da plataforma',
  },
  'brand.settings.colSetting': { en: 'Setting', es: 'Ajuste', 'pt-BR': 'Configuração' },
  'brand.settings.colValue': { en: 'Value', es: 'Valor', 'pt-BR': 'Valor' },
  'brand.settings.propose': { en: 'Propose a change', es: 'Proponer un cambio', 'pt-BR': 'Propor uma alteração' },
  'brand.settings.change': { en: 'Change', es: 'Cambiar', 'pt-BR': 'Alterar' },
  'brand.settings.saveNow': { en: 'Save now', es: 'Guardar ahora', 'pt-BR': 'Salvar agora' },
  'brand.settings.saving': { en: 'Saving…', es: 'Guardando…', 'pt-BR': 'Salvando…' },
  'brand.settings.proposeInstead': { en: 'Propose to the operator instead', es: 'Proponer al operador en su lugar', 'pt-BR': 'Propor ao operador em vez disso' },
  'brand.settings.savedNow': { en: '{key} was changed. The operator has been told.', es: 'Se cambió {key}. Se avisó al operador.', 'pt-BR': '{key} foi alterado. O operador foi avisado.' },
  'brand.settings.saveNowNote': {
    en: 'Save now changes the setting at once, from the date above, recorded under your name with your reason, and tells the operator. Propose instead sends it for the operator to approve.',
    es: 'Guardar ahora cambia el ajuste de inmediato, desde la fecha indicada, queda registrado a tu nombre con tu motivo y se avisa al operador. Proponer lo envía para que el operador lo apruebe.',
    'pt-BR': 'Salvar agora altera a configuração na hora, a partir da data acima, fica registrado em seu nome com o seu motivo e o operador é avisado. Propor envia para o operador aprovar.',
  },
  'brand.settings.history': { en: 'History', es: 'Historial', 'pt-BR': 'Histórico' },

  // Propose a setting value
  'brand.propose.valueField': { en: 'Value for this brand', es: 'Valor para esta marca', 'pt-BR': 'Valor para esta marca' },
  'brand.propose.appliesFrom': { en: 'Applies from', es: 'Se aplica desde', 'pt-BR': 'Aplica-se a partir de' },
  'brand.propose.appliesHint': {
    en: 'Left as today, it applies as soon as the operator approves it. A later day starts at midnight.',
    es: 'Si se deja como hoy, se aplica en cuanto el operador lo aprueba. Un día posterior empieza a medianoche.',
    'pt-BR': 'Se ficar como hoje, vale assim que o operador aprovar. Um dia posterior começa à meia-noite.',
  },
  'brand.propose.why': { en: 'Why', es: 'Motivo', 'pt-BR': 'Motivo' },
  'brand.propose.whyHint': {
    en: 'The operator reads this before deciding, and it becomes the reason on their own audit record.',
    es: 'El operador lo lee antes de decidir y pasa a ser el motivo en su propio registro de auditoría.',
    'pt-BR': 'O operador lê isto antes de decidir, e passa a ser o motivo no registro de auditoria dele.',
  },
  'brand.propose.whyPlaceholder': {
    en: 'Ticket number and what was asked for',
    es: 'Número de ticket y qué se pidió',
    'pt-BR': 'Número do ticket e o que foi pedido',
  },
  'brand.propose.proposing': { en: 'Proposing…', es: 'Proponiendo…', 'pt-BR': 'Propondo…' },
  'brand.propose.value': { en: 'Propose this value', es: 'Proponer este valor', 'pt-BR': 'Propor este valor' },
  'brand.propose.inherit': {
    en: 'Propose inheriting again ({value})',
    es: 'Proponer volver a heredar ({value})',
    'pt-BR': 'Propor voltar a herdar ({value})',
  },
  'brand.propose.clearBlocked': {
    en: 'Clearing takes effect as soon as the operator approves it, not on the date above. Set "Applies from" back to today to clear it, or propose a new value for that date instead.',
    es: 'Quitar el valor entra en vigor en cuanto el operador lo aprueba, no en la fecha de arriba. Vuelve a poner "Se aplica desde" en hoy para quitarlo, o propón un valor nuevo para esa fecha.',
    'pt-BR': 'Limpar o valor passa a valer assim que o operador aprovar, não na data acima. Volte "Aplica-se a partir de" para hoje para limpar, ou proponha um novo valor para essa data.',
  },
  'brand.propose.noChange': {
    en: 'Nothing changes when you press this. The operator is notified, and one of their own people approves or rejects it — the value is recorded against whoever approves, not against you. Unanswered proposals lapse after a fortnight.',
    es: 'No cambia nada al pulsar esto. Se avisa al operador y una de sus personas lo aprueba o lo rechaza: el valor queda registrado a nombre de quien aprueba, no a tu nombre. Las propuestas sin respuesta caducan a las dos semanas.',
    'pt-BR': 'Nada muda ao clicar aqui. O operador é notificado e uma pessoa da equipe dele aprova ou rejeita: o valor fica registrado em nome de quem aprova, não no seu. Propostas sem resposta expiram após duas semanas.',
  },

  // Setting history
  'brand.history.title': { en: 'History — {key}', es: 'Historial — {key}', 'pt-BR': 'Histórico — {key}' },
  'brand.history.empty': {
    en: 'Nothing has changed this setting at this brand yet.',
    es: 'Todavía nada ha cambiado este ajuste en esta marca.',
    'pt-BR': 'Nada mudou esta configuração nesta marca ainda.',
  },
  'brand.history.colFrom': { en: 'From', es: 'Desde', 'pt-BR': 'De' },
  'brand.history.colState': { en: 'State', es: 'Estado', 'pt-BR': 'Estado' },
  'brand.history.colUntil': { en: 'Until', es: 'Hasta', 'pt-BR': 'Até' },
  'brand.history.colValue': { en: 'Value', es: 'Valor', 'pt-BR': 'Valor' },
  'brand.history.colLevel': { en: 'Level', es: 'Nivel', 'pt-BR': 'Nível' },
  'brand.history.colSetBy': { en: 'Set by', es: 'Definido por', 'pt-BR': 'Definido por' },
  'brand.history.colWhy': { en: 'Why', es: 'Motivo', 'pt-BR': 'Motivo' },
  'brand.history.withdrawn': {
    en: 'Withdrawn {when} by {who}',
    es: 'Retirado el {when} por {who}',
    'pt-BR': 'Retirado em {when} por {who}',
  },
  'brand.history.ongoing': { en: 'Ongoing', es: 'En curso', 'pt-BR': 'Em andamento' },
  'brand.history.levelBrand': { en: 'Brand', es: 'Marca', 'pt-BR': 'Marca' },
  'brand.history.levelOperator': { en: 'Operator', es: 'Operador', 'pt-BR': 'Operador' },

  // Propose a profile change
  'brand.profile.displayName': { en: 'Display name', es: 'Nombre visible', 'pt-BR': 'Nome de exibição' },
  'brand.profile.baseCurrency': { en: 'Base currency', es: 'Moneda base', 'pt-BR': 'Moeda base' },
  'brand.profile.currencyHint': {
    en: 'Deals, control feeds and commission figures follow it from the moment it is approved.',
    es: 'Los acuerdos, los feeds de control y las cifras de comisión la siguen desde el momento en que se aprueba.',
    'pt-BR': 'Os acordos, os feeds de controle e os números de comissão a seguem a partir do momento em que ela é aprovada.',
  },
  'brand.profile.why': { en: 'Why', es: 'Motivo', 'pt-BR': 'Motivo' },
  'brand.profile.whyHint': {
    en: 'The operator reads this before deciding. A request with no reason is one nobody can weigh.',
    es: 'El operador lo lee antes de decidir. Una solicitud sin motivo es una que nadie puede valorar.',
    'pt-BR': 'O operador lê isto antes de decidir. Um pedido sem motivo é um pedido que ninguém consegue avaliar.',
  },
  'brand.profile.whyPlaceholder': {
    en: 'Ticket number and what was asked for',
    es: 'Número de ticket y qué se pidió',
    'pt-BR': 'Número do ticket e o que foi pedido',
  },
  'brand.profile.proposing': { en: 'Proposing…', es: 'Proponiendo…', 'pt-BR': 'Propondo…' },
  'brand.profile.propose': { en: 'Propose this change', es: 'Proponer este cambio', 'pt-BR': 'Propor esta alteração' },
  'brand.profile.noChange': {
    en: 'Nothing changes when you press this. The operator is notified, and one of their own people approves or rejects it — the change is recorded against whoever approves, not against you. Unanswered proposals lapse after a fortnight.',
    es: 'No cambia nada al pulsar esto. Se avisa al operador y una de sus personas lo aprueba o lo rechaza: el cambio queda registrado a nombre de quien aprueba, no a tu nombre. Las propuestas sin respuesta caducan a las dos semanas.',
    'pt-BR': 'Nada muda ao clicar aqui. O operador é notificado e uma pessoa da equipe dele aprova ou rejeita: a alteração fica registrada em nome de quem aprova, não no seu. Propostas sem resposta expiram após duas semanas.',
  },
} as const satisfies Catalogue;
